import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import debounce from 'debounce';
import { useLocale } from 'next-intl';

import { searchSocket } from '@/shared/api/sockets/modules/search';
import type { SearchDataResponse } from '@/shared/api/sockets/modules/search';
import type { LanguageCodeEnum } from '@/shared/config/common/constants/lang';

interface UseSearchHeaderReturn {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  isDropdownOpen: boolean;
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  searchResults: SearchDataResponse<['doctors', 'medcenters', 'skills']> | null;
  isEmptySearch: boolean;
  loadNewPage: boolean;
  setLoadNewPage: React.Dispatch<React.SetStateAction<boolean>>;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchClick: () => void;
  searchRef: React.RefObject<HTMLDivElement>;
}

/**
 * Хук для управления логикой поиска в заголовке
 *
 * @returns Объект с состояниями и обработчиками для поиска
 */
export const useSearchHeader = (): UseSearchHeaderReturn => {
  const locale = useLocale();
  const searchRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchDataResponse<['doctors', 'medcenters', 'skills']> | null>(
    null,
  );
  const [loadNewPage, setLoadNewPage] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      searchSocket.disconnect();
    };
  }, []);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      if (searchQuery.length > 2) {
        setIsLoading(true);

        searchSocket.search(
          {
            query: searchQuery,
            indexes: ['doctors', 'medcenters', 'skills'],
            language: locale as LanguageCodeEnum,
          },
          {
            onSuccess: response => {
              setSearchResults(response.data);
              setIsLoading(false);
            },
            onError: () => {
              setIsLoading(false);
            },
          },
        );
      }
    }, 400),
    [],
  );

  const isEmptySearch = useMemo(() => {
    return query.length > 2 && searchResults
      ? Object.values(searchResults).every(value => value.data.length === 0)
      : true;
  }, [query, searchResults]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);

      if (event.target.value.length > 2) {
        setIsDropdownOpen(true);
        debouncedSearch(event.target.value);
      }
    },
    [debouncedSearch],
  );

  const handleSearchClick = useCallback(() => {
    searchSocket.connect();

    if (query.length > 2) {
      setIsDropdownOpen(true);
    }
  }, [query]);

  return {
    query,
    setQuery,
    isDropdownOpen,
    setIsDropdownOpen,
    isLoading,
    searchResults,
    isEmptySearch,
    loadNewPage,
    setLoadNewPage,
    handleSearchChange,
    handleSearchClick,
    searchRef,
  };
};
